
'use client';

import type { BlogPostSerializable } from '@/services/blogService';
import { useState } from 'react';
import Image from 'next/image';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CalendarDays, UserCircle, ArrowLeft, Clock } from 'lucide-react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import MarkdownRenderer, { type Heading } from '@/components/blog/MarkdownRenderer';
import BlogTableOfContents from '@/components/blog/BlogTableOfContents';
import AuthorCard from '@/components/blog/AuthorCard';

interface BlogPostClientPageProps {
  post: BlogPostSerializable;
}

// This is the new Client Component that handles all the interactive parts.
export default function BlogPostClientPage({ post }: BlogPostClientPageProps) {
  const [headings, setHeadings] = useState<Heading[]>([]);

  // Robuste Lösung: Stellt sicher, dass jeder Beitrag ein H1-Tag hat.
  // Wenn der aus der DB abgerufene Inhalt nicht mit einem Markdown-H1 beginnt,
  // wird der Titel des Beitrags als H1 vorangestellt.
  let contentToRender = post.content;
  if (post.content && !post.content.trim().startsWith('# ')) {
    contentToRender = `# ${post.title}\n\n${post.content}`;
  }

  // Schema.org JSON-LD (dynamisch im Head, wenn post verfügbar ist)
  const authorName = post.author_profile?.name ?? post.author;
  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${process.env.NEXT_PUBLIC_BASE_URL || 'https://www.luehmann-it.de'}/blog/${post.slug}`,
    },
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image_url,
    "author": {
      "@type": "Person",
      "name": authorName,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Luehmann-IT",
      "logo": {
        "@type": "ImageObject",
        "url": `${process.env.NEXT_PUBLIC_BASE_URL || 'https://www.luehmann-it.de'}/img/logo-transparent.png`,
      },
    },
    "datePublished": post.published_at,
    "dateModified": post.published_at,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingSchema) }}
        key="blog-posting-schema"
      />
      <div className="max-w-[1180px] mx-auto px-4 sm:px-8 py-[90px]">
        <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-12 xl:grid-cols-[320px_1fr] xl:gap-16">
          {/* Table of Contents Sidebar/Drawer */}
          <div>
            <BlogTableOfContents headings={headings} />
          </div>

          {/* Main Content */}
          <div className="min-w-0">
            <Card className="shadow-lg bg-card border-border">
              <CardHeader className="relative p-0">
                {post.image_url && (
                  <div className="relative w-full h-64 md:h-96 rounded-t-lg overflow-hidden">
                    <Image
                      src={post.image_url}
                      alt={post.title}
                      fill
                      className="object-cover saturate-[0.85] contrast-105"
                      priority
                      sizes="(max-width: 896px) 100vw, 896px"
                      data-ai-hint={post.image_ai_hint || "blog article"}
                    />
                  </div>
                )}
                {post.image_url && post.image_from && (
                  <div className="px-6 pt-2 text-right">
                    <p className="text-xs text-muted-foreground italic">
                      Bildquelle: {post.image_from}
                    </p>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[0.72rem] uppercase tracking-[0.06em]" style={{ color: 'var(--signal)' }}>
                    <span className="flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {post.publishedAtFormatted}
                    </span>
                    <span className="text-[#8794A0] flex items-center gap-1.5">
                      <UserCircle className="h-3.5 w-3.5" />
                      {authorName}
                    </span>
                    <span className="text-[#8794A0] flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {post.readingTimeMinutes} Min Lesezeit
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent id="article-content" className="p-6 pt-0 md:p-8 md:pt-0 space-y-6 font-body text-foreground/90 prose prose-lg max-w-none prose-headings:text-primary prose-headings:font-display prose-a:text-accent hover:prose-a:text-accent/80 prose-strong:text-foreground/95">
                {typeof contentToRender === 'string' ? (
                  <MarkdownRenderer content={contentToRender} onHeadingsExtracted={setHeadings} />
                ) : <p>Inhalt nicht verfügbar.</p>}
                {post.author_profile && (
                  <div className="not-prose">
                    <AuthorCard author={post.author_profile} />
                  </div>
                )}
              </CardContent>
              <div className="p-6 md:p-8 border-t border-border">
                <Button asChild variant="outline">
                  <Link href="/blog">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Zurück zur Blogübersicht
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
